<div class="container" [formGroup]="formGroup">
  <pdq-card variant="popover" color="white">
    <div class="card-content">
      <div class="section-header">
        <pdq-heading element="h6">Layout Style</pdq-heading>
        <span class="section-subtitle">Choose how reviews appear in the checkout experience</span>
      </div>
      <pdq-divider></pdq-divider>
      <div class="layout-options">
        @for (option of layoutOptions; track option.value) {
          <div
            class="layout-option"
            [class.selected]="currentLayoutStyle === option.value"
            (click)="selectLayout(option.value)">
            @if (option.recommended) {
              <pdq-tag class="recommended-badge" value="Recommended" variant="info" mode="rect" size="small"> </pdq-tag>
            }
            <pdq-radio-group>
              <pdq-radio-button
                [checked]="currentLayoutStyle === option.value"
                [text]="option.label"
                [subText]="option.description">
              </pdq-radio-button>
            </pdq-radio-group>
            @if (option.value === LayoutStyle.RATING_BADGE && isRatingBadge) {
              <pdq-link
                class="setup-guide-link"
                label="Setup Guide"
                url="https://support.prettydamnquick.com/en/articles/13935616-pdq-dynamic-reviews"
                [external]="true"
                size="xsmall">
              </pdq-link>
            }
          </div>
        }
      </div>
    </div>
  </pdq-card>

  @if (isRatingBadge) {
    <pdq-card variant="popover" color="white" formGroupName="ratingBadgeConfig">
      <div class="card-content">
        <div><pdq-heading element="h6">Configure</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="configure-section">
          <span class="configure-section-label">Type</span>
          <pdq-radio-group formControlName="type">
            @for (option of ratingBadgeTypeOptions; track option.value) {
              <pdq-radio-button
                [value]="option.value"
                [text]="option.label"
                [checked]="formGroup.get('ratingBadgeConfig.type')?.value === option.value">
              </pdq-radio-button>
            }
          </pdq-radio-group>
        </div>
        <div class="configure-section">
          <span class="configure-section-label">Design</span>
          <pdq-input-color label="Star Color" formControlName="starColor"></pdq-input-color>
        </div>
      </div>
    </pdq-card>
  }

  @if (isCarousel) {
    <pdq-card variant="popover" color="white" formGroupName="carouselConfig">
      <div class="card-content">
        <div><pdq-heading element="h6">Configure</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="settings-grid">
          <pdq-radio-group formControlName="navigationStyle" label="Navigation Style">
            @for (option of carouselNavigationOptions; track option.value) {
              <pdq-radio-button
                [value]="option.value"
                [text]="option.label"
                [subText]="option.description"
                [checked]="formGroup.get('carouselConfig.navigationStyle')?.value === option.value">
              </pdq-radio-button>
            }
          </pdq-radio-group>
          <div class="setting-row full-width">
            <pdq-input label="Title" formControlName="title" [placeholder]="carouselDefaultTitle"></pdq-input>
          </div>
          <div class="setting-row full-width">
            <pdq-radio-group formControlName="starPosition" label="Show Stars Rating">
              @for (option of starPositionOptions; track option.value) {
                <pdq-radio-button
                  [value]="option.value"
                  [text]="option.label"
                  [checked]="formGroup.get('carouselConfig.starPosition')?.value === option.value">
                </pdq-radio-button>
              }
            </pdq-radio-group>
          </div>
          <div class="setting-row full-width">
            <label class="setting-label">Review Content</label>
            @if (carouselContentControl) {
              <rich-text-editor
                [control]="carouselContentControl"
                [placeholder]="'Enter review content template...'"
                [config]="dynamicFieldsMentionConfig"
                [invalidMsg]="carouselContentError">
              </rich-text-editor>
            }
            <span class="helper-text">Dynamic fields available. Type &#123; to insert dynamic fields</span>
          </div>
        </div>
      </div>

      <div class="card-content" formGroupName="design">
        <div class="design-section">
          <span class="design-section-label">Design</span>
          <pdq-input-color label="Star Color" formControlName="starColor" [disabled]="isStarHidden"></pdq-input-color>
          <pdq-dropdown title="Background Color" [options]="backgroundColorOptions" formControlName="backgroundColor">
          </pdq-dropdown>
          <div class="design-grid">
            <pdq-dropdown title="Border" [options]="borderOptions" formControlName="border"></pdq-dropdown>
            <pdq-dropdown
              title="Border Width"
              [options]="borderWidthOptions"
              formControlName="borderWidth"></pdq-dropdown>
            <pdq-dropdown
              title="Corner Radius"
              [options]="cornerRadiusOptions"
              formControlName="cornerRadius"></pdq-dropdown>
            <pdq-dropdown title="Padding" [options]="paddingOptions" formControlName="padding"></pdq-dropdown>
          </div>
        </div>
      </div>
    </pdq-card>
  }

  @if (isList) {
    <pdq-card variant="popover" color="white" formGroupName="listConfig">
      <div class="card-content">
        <div><pdq-heading element="h6">Configure</pdq-heading></div>
        <pdq-divider></pdq-divider>
        <div class="settings-grid">
          <div class="setting-row full-width">
            <pdq-input label="Title" formControlName="title" [placeholder]="listDefaultTitle"></pdq-input>
          </div>
          <div class="setting-row full-width">
            <label class="setting-label">Description Template</label>
            @if (listDescriptionControl) {
              <rich-text-editor
                [control]="listDescriptionControl"
                [placeholder]="'Enter description template...'"
                [config]="dynamicFieldsMentionConfig"
                [invalidMsg]="listDescriptionError">
              </rich-text-editor>
            }
            <span class="helper-text">Dynamic fields available. Type &#123; to insert dynamic fields</span>
          </div>
        </div>
      </div>

      <div class="card-content" formGroupName="design">
        <div class="design-section">
          <span class="design-section-label">Design</span>
          <pdq-input-color label="Star Color" formControlName="starColor"></pdq-input-color>
          <pdq-dropdown title="Background Color" [options]="backgroundColorOptions" formControlName="backgroundColor">
          </pdq-dropdown>
          <div class="design-grid">
            <pdq-dropdown title="Border" [options]="borderOptions" formControlName="border"></pdq-dropdown>
            <pdq-dropdown
              title="Border Width"
              [options]="borderWidthOptions"
              formControlName="borderWidth"></pdq-dropdown>
            <pdq-dropdown
              title="Corner Radius"
              [options]="cornerRadiusOptions"
              formControlName="cornerRadius"></pdq-dropdown>
            <pdq-dropdown title="Padding" [options]="paddingOptions" formControlName="padding"></pdq-dropdown>
          </div>
        </div>
      </div>

      <div class="card-content">
        <div class="reviews-count-section">
          <span class="reviews-count-title">Pagination</span>
          <div class="reviews-count-row">
            <div class="reviews-count-row-labels">
              <span class="reviews-count-text">Paginate after</span>
              <span class="reviews-count-sublabel">Reviews per page</span>
            </div>
            <pdq-input-arrows formControlName="reviewsPerPage" [min]="1" widthSize="small"></pdq-input-arrows>
            <span class="reviews-count-text">reviews</span>
          </div>
        </div>
      </div>
    </pdq-card>
  }
</div>
